// pms-core / domain — « Tetris » v2: which bookings to move to open a room.
//
// docs/PMS-TETRIS-BRIEF.md. v1 (`proposeRoomAssignment`) answers « which room is
// free for this stay ». This answers the question it leaves open: no room is free
// for the whole stay, so which bookings move to make one, and if none can, what
// gives. A FUNCTIONAL CORE (ARCHITECTURE.md §1): it proposes, never assigns.
// Until cutover the desk performs the moves in Vega; after it, one atomic RPC
// will (brief §8).
//
// ── The search, in order, at each level of rules (brief §5) ────────────────
//
//   1. a room free for the whole stay (0 moves) — v1's question, asked again
//      here because a level with a rule lifted may open one;
//   2. EJECTION CHAINS: put the stay in a room, the stays it overlaps there go
//      elsewhere, which may displace others, up to K moved stays. The search
//      deepens one move at a time, so the first depth with any plan is the
//      fewest moves; every plan at that depth is ranked and the best three kept;
//   3. a SPLIT: the stay changes room mid-stay, nobody else moves, with the
//      fewest changes (the classic interval-covering greedy: from each night,
//      the free room whose run reaches furthest).
//
// ── The rules, ranked (brief §4, operator 2026-10-01) ──────────────────────
//
// When nothing works, the lowest-ranked rule is lifted and the search runs
// again, then the next. Order, operator Q13: the BED first (« bed configs are
// on an availability basis from the start »), then the FREEZE (today's arrivals
// and « ne pas déplacer » — « it is always OK to move a not-yet-arrived
// reservation »), then the MOVE LIMIT, and « never below the sold category »
// last. Never lifted: the party fits the room (« top rank »), one stay per room
// per night, in-house guests stay put. Every plan names the rules it bends and
// for whom, so nothing is bent silently. When even that finds nothing, the
// answer is the shortfall per night, and what to do about it is the desk's.
//
// ── What it refuses to guess ───────────────────────────────────────────────
//
// A stay whose nights were not all READ (`isFullyRead`) is never moved: its
// new room's other bookings on those nights are unknown. The rack reads a
// bounded period (ADR-0022), so this is a live case, not a theoretical one.

import { pmsAddDays, pmsNightCount, type PmsBoard } from './pms.js';
import { ASSIGNMENT_SMALL_HOLE_NIGHTS, type AssignableRoom, type AssignableStay } from './roomAssignment.js';
import { bedStandsFor, categoryDominates, roomMeetsBed, soldCategory, type RoomFit } from './roomFit.js';
import type { IsoDate, RoomId, RoomStay } from './types.js';

/** A rule the planner may lift when the book is over capacity. */
export type ReshuffleRule = 'bed' | 'freeze' | 'maxMoves' | 'belowSold';

/** The order rules are lifted in (operator Q13, 2026-10-01): the bed first, a
 *  downgrade below the sold category last. A setting, not a law. */
export const RESHUFFLE_LIFT_ORDER: readonly ReshuffleRule[] = ['bed', 'freeze', 'maxMoves', 'belowSold'];

/** The most stays one plan may move (operator Q2: « K=3 is fine »; G1: 98 %). */
export const RESHUFFLE_MAX_MOVES = 3;
/** …and once that rule is lifted in an overbooking. Bounded all the same: the
 *  search must answer while the desk waits. */
export const RESHUFFLE_LIFTED_MAX_MOVES = 5;

/** A stay as the planner needs it: v1's shape plus where it stands. */
export type PlannerStay = AssignableStay & Pick<RoomStay, 'status' | 'ratePlanId'>;

export interface ReshuffleOptions {
  maxMoves?: number;
  liftedMaxMoves?: number;
  liftOrder?: readonly ReshuffleRule[];
  /** « Ne pas déplacer » — the desk's veto on a proposed move, the stand-in
   *  for the staff freeze until it exists (brief §3). */
  excluded?: ReadonlySet<string>;
  /** Whether every night of a stay was read. Default: all were. */
  isFullyRead?: (s: PlannerStay) => boolean;
  /** The board a rate plan sells, for the upgrade priority (brief §6.4). */
  boardOf?: (ratePlanId: number) => PmsBoard | null;
}

/** How a stay ends up relative to what it bought. */
export type PlacementKind = 'same' | 'upgrade' | 'downgrade' | 'below';

export interface ReshuffleMove {
  stayId: string;
  fromRoomId: RoomId;
  toRoomId: RoomId;
  kind: PlacementKind;
  /** The new room already stands made up as the stay asked (null: no request,
   *  or not known). false is a bed change for the étage. */
  bedReady: boolean | null;
}

/** A rule a plan bends, and for whom. */
export interface RuleBend {
  rule: ReshuffleRule;
  stayId: string;
}

export interface ReshufflePlan {
  targetRoomId: RoomId;
  targetKind: PlacementKind;
  targetBedReady: boolean | null;
  /** Stays to take OUT of their room before anything else (« désaffecter »),
   *  because the plan swaps them in a cycle: A into B's room and B into A's.
   *  No order of plain moves performs a swap — Vega would refuse the first one
   *  — so one of the two is released first and allocated again in its turn.
   *  Empty for every plan without a cycle. */
  released: string[];
  /** In the order the desk performs them, AFTER the releases: every move's
   *  destination is free when its turn comes, so the list can be followed in
   *  Vega as written. A released stay's move is its re-allocation. */
  moves: ReshuffleMove[];
  bends: RuleBend[];
  /** The ranking vector, lower is better, in the approved order (brief §5):
   *  bends, moves, tomorrow's arrivals moved, bed changes (net), stranded
   *  nights, off the group's floor, the room number, then the upgrade priority
   *  (which decides between plans for the same room). Exposed so a test can say
   *  WHY one plan beat another. */
  cost: number[];
}

export interface SplitSegment {
  roomId: RoomId;
  from: IsoDate;
  /** Exclusive: the night of `to` is the next segment's. */
  to: IsoDate;
}

export type ReshuffleResult =
  /** Plans, best first (at most three), and the rules lifted to find them. A
   *  plan may have no moves when lifting a rule opened a room. */
  | { kind: 'plans'; plans: ReshufflePlan[]; lifted: ReshuffleRule[] }
  | { kind: 'split'; segments: SplitSegment[]; bends: RuleBend[]; lifted: ReshuffleRule[] }
  /** Full: on these nights the hotel lacks this many rooms for everyone it has
   *  promised a bed to, even with every liftable rule lifted. */
  | { kind: 'overbooked'; shortfall: { date: IsoDate; missing: number }[] }
  /** Not full, but nothing within the move limit, even lifted. */
  | { kind: 'unresolved' };

const TARGET = '__target__';
const BOARD_RANK: Record<string, number> = { hb: 0, bb: 1, none: 2 };
const PLAN_CAP = 400;
/** Relocation steps one level may take before it stops looking. Measured on
 *  the real book (G1) no request came near it; it exists for the lifted levels,
 *  where five moves over 42 rooms could otherwise run while the desk waits. */
const STEP_BUDGET = 200_000;

const overlaps = (a: { checkinDate: string; checkoutDate: string }, b: { checkinDate: string; checkoutDate: string }) =>
  a.checkinDate < b.checkoutDate && b.checkinDate < a.checkoutDate;

export function planRoomReshuffle(
  target: PlannerStay,
  rooms: readonly AssignableRoom[],
  stays: readonly PlannerStay[],
  todayIso: IsoDate,
  fit: RoomFit,
  options: ReshuffleOptions = {},
): ReshuffleResult {
  const order = options.liftOrder ?? RESHUFFLE_LIFT_ORDER;
  for (let level = 0; level <= order.length; level += 1) {
    const lifted = new Set(order.slice(0, level));
    const planner = new Planner(target, rooms, stays, todayIso, fit, options, lifted);
    // A night that cannot seat everyone under this level's rules rules out
    // every plan and every split at this level: no need to search.
    if (planner.deficits().length > 0) continue;
    const plans = planner.plans();
    if (plans.length > 0) return { kind: 'plans', plans, lifted: [...lifted] };
    const split = planner.split();
    if (split !== null) return { kind: 'split', ...split, lifted: [...lifted] };
  }
  const shortfall = new Planner(target, rooms, stays, todayIso, fit, options, new Set(order)).deficits();
  return shortfall.length > 0 ? { kind: 'overbooked', shortfall } : { kind: 'unresolved' };
}

class Planner {
  private readonly byId = new Map<string, PlannerStay>();
  private readonly occ = new Map<RoomId, Set<string>>();
  private readonly roomById: Map<RoomId, AssignableRoom>;
  private readonly typed: AssignableRoom[];
  private readonly tomorrow: IsoDate;
  private readonly k: number;
  private readonly siblingFloors: number[];
  private steps = 0;

  constructor(
    private readonly target: PlannerStay,
    rooms: readonly AssignableRoom[],
    private readonly stays: readonly PlannerStay[],
    private readonly today: IsoDate,
    private readonly fit: RoomFit,
    private readonly options: ReshuffleOptions,
    private readonly lifted: ReadonlySet<ReshuffleRule>,
  ) {
    this.roomById = new Map(rooms.map((r) => [r.id, r]));
    this.typed = rooms.filter((r) => r.roomTypeId !== null);
    for (const r of rooms) this.occ.set(r.id, new Set());
    for (const s of stays) {
      if (s.id === target.id || s.roomId === null || !this.roomById.has(s.roomId)) continue;
      this.byId.set(s.id, s);
      this.occ.get(s.roomId)!.add(s.id);
    }
    this.byId.set(TARGET, target);
    this.tomorrow = pmsAddDays(today, 1);
    this.k = lifted.has('maxMoves')
      ? (options.liftedMaxMoves ?? RESHUFFLE_LIFTED_MAX_MOVES)
      : (options.maxMoves ?? RESHUFFLE_MAX_MOVES);
    this.siblingFloors = stays
      .filter((s) => s.reservationId === target.reservationId && s.id !== target.id && s.roomId !== null)
      .map((s) => this.roomById.get(s.roomId!)?.floor)
      .filter((f): f is number => f !== undefined);
  }

  // ── The rules ─────────────────────────────────────────────────────────────

  /** Never movable, whatever is lifted: arrived, departed, or not fully read. */
  private pinned(s: PlannerStay): boolean {
    if (s.status === 'checked_in' || s.status === 'checked_out') return true;
    if (s.checkinDate < this.today) return true;
    return this.options.isFullyRead !== undefined && !this.options.isFullyRead(s);
  }

  /** Held by the freeze: today's arrivals and « ne pas déplacer ». */
  private frozen(s: PlannerStay): boolean {
    return s.checkinDate === this.today || (this.options.excluded?.has(s.id) ?? false);
  }

  private fixed(id: string): boolean {
    if (id === TARGET) return true;
    const s = this.byId.get(id)!;
    return this.pinned(s) || (!this.lifted.has('freeze') && this.frozen(s));
  }

  /** N1 (the party fits) and the liftable category and bed rules. A stay may
   *  always stay in its own room — history can break a rule we model. */
  private allows(s: PlannerStay, room: AssignableRoom): boolean {
    if (room.roomTypeId === null) return false;
    if (s.id !== TARGET && s.roomId === room.id) return true;
    if (room.maxOccupancy !== null && room.maxOccupancy < s.adults + s.children) return false;
    if (!this.lifted.has('belowSold') && !categoryDominates(this.fit, room.roomTypeId, soldCategory(s))) return false;
    if (!this.lifted.has('bed') && !roomMeetsBed(this.fit, room, s.requestedBedConfig)) return false;
    return true;
  }

  private conflicts(roomId: RoomId, s: { checkinDate: string; checkoutDate: string }): string[] {
    return [...this.occ.get(roomId)!].filter((id) => overlaps(this.byId.get(id)!, s));
  }

  // ── Ejection chains ───────────────────────────────────────────────────────

  plans(): ReshufflePlan[] {
    const T = this.target;
    const candidates = this.typed.filter((r) => this.allows({ ...T, id: TARGET }, r));
    for (let depth = 0; depth <= this.k; depth += 1) {
      const found: { room: AssignableRoom; chain: { stayId: string; from: RoomId; to: RoomId }[] }[] = [];
      for (const room of candidates) {
        if (found.length >= PLAN_CAP) break;
        const displaced = this.conflicts(room.id, T);
        if (displaced.length > depth || displaced.some((id) => this.fixed(id))) continue;
        const holder = this.occ.get(room.id)!;
        for (const id of displaced) holder.delete(id);
        holder.add(TARGET);
        for (const chain of this.relocate(displaced, depth - displaced.length, new Set(displaced), [])) {
          // Only plans of exactly this depth: a shallower one would have been
          // found at its own depth, so « fewest moves » holds by construction.
          if (chain.length === depth) found.push({ room, chain });
          if (found.length >= PLAN_CAP) break;
        }
        holder.delete(TARGET);
        for (const id of displaced) holder.add(id);
      }
      if (found.length > 0) {
        return found
          .map((f) => this.toPlan(f.room, f.chain))
          .sort(comparePlans)
          .slice(0, 3);
      }
    }
    return [];
  }

  private *relocate(
    queue: string[],
    budget: number,
    moved: Set<string>,
    chain: { stayId: string; from: RoomId; to: RoomId }[],
  ): Generator<{ stayId: string; from: RoomId; to: RoomId }[]> {
    if (queue.length === 0) {
      yield chain.slice();
      return;
    }
    const [id, ...rest] = queue as [string, ...string[]];
    const s = this.byId.get(id)!;
    for (const room of this.typed) {
      if ((this.steps += 1) > STEP_BUDGET) return;
      if (room.id === s.roomId || !this.allows(s, room)) continue;
      const out = this.conflicts(room.id, s);
      // `moved`: each stay moves at most once. At the fewest-moves depth this
      // changes no answer — a plan moving X twice always has a shorter one
      // moving X straight to its last room, found a depth earlier (mutation
      // testing confirmed the rule survives its own removal for that reason).
      // It stays because it prunes, and because the brief promises it.
      if (out.length > budget || out.some((e) => this.fixed(e) || moved.has(e))) continue;
      const holder = this.occ.get(room.id)!;
      for (const e of out) holder.delete(e);
      holder.add(id);
      chain.push({ stayId: id, from: s.roomId!, to: room.id });
      yield* this.relocate([...rest, ...out], budget - out.length, new Set([...moved, ...out]), chain);
      chain.pop();
      holder.delete(id);
      for (const e of out) holder.add(e);
    }
  }

  // ── Ranking ───────────────────────────────────────────────────────────────

  private kindOf(s: PlannerStay, roomId: RoomId): PlacementKind {
    const room = this.roomById.get(roomId)!;
    // Against what the stay was SOLD as, not the type of the room it is in: a stay the desk
    // upgraded in Vega is « in a better room than it bought », and going back is a downgrade.
    const sold = soldCategory(s);
    if (room.roomTypeId === sold) {
      const home = s.roomId === null ? undefined : this.roomById.get(s.roomId);
      return home !== undefined && home.roomTypeId !== sold ? 'downgrade' : 'same';
    }
    return categoryDominates(this.fit, room.roomTypeId, sold) ? 'upgrade' : 'below';
  }

  private bendsOf(s: PlannerStay, roomId: RoomId, moved: boolean): RuleBend[] {
    const room = this.roomById.get(roomId)!;
    const id = s.id;
    const out: RuleBend[] = [];
    if (!roomMeetsBed(this.fit, room, s.requestedBedConfig)) out.push({ rule: 'bed', stayId: id });
    if (!categoryDominates(this.fit, room.roomTypeId, soldCategory(s))) out.push({ rule: 'belowSold', stayId: id });
    if (moved && this.frozen(s)) out.push({ rule: 'freeze', stayId: id });
    return out;
  }

  /** Stranded 1–2-night gaps either side of `s` in `roomId`, in the final
   *  layout (v1's rule: both edges are stays, and the gap lies ahead of today). */
  private stranded(id: string, roomId: RoomId, layout: Map<string, RoomId>): number {
    const s = this.byId.get(id)!;
    const others = [...layout.entries()]
      .filter(([other, r]) => r === roomId && other !== id)
      .map(([other]) => this.byId.get(other)!);
    const prior = others.filter((o) => o.checkoutDate <= s.checkinDate).sort((a, b) => b.checkoutDate.localeCompare(a.checkoutDate))[0];
    const next = others.filter((o) => o.checkinDate >= s.checkoutDate).sort((a, b) => a.checkinDate.localeCompare(b.checkinDate))[0];
    const small = (n: number) => n >= 1 && n <= ASSIGNMENT_SMALL_HOLE_NIGHTS;
    let n = 0;
    if (prior !== undefined && prior.checkoutDate >= this.today && small(pmsNightCount(prior.checkoutDate, s.checkinDate))) n += 1;
    if (next !== undefined && small(pmsNightCount(s.checkoutDate, next.checkinDate))) n += 1;
    return n;
  }

  private toPlan(room: AssignableRoom, chain: { stayId: string; from: RoomId; to: RoomId }[]): ReshufflePlan {
    const T = this.target;
    // The final layout: every placed stay where it ends up, the target included.
    const layout = new Map<string, RoomId>();
    for (const [rid, ids] of this.occ) for (const id of ids) if (id !== TARGET) layout.set(id, rid);
    for (const m of chain) layout.set(m.stayId, m.to);
    layout.set(TARGET, room.id);

    const moves: ReshuffleMove[] = chain.map((m) => {
      const s = this.byId.get(m.stayId)!;
      return {
        stayId: m.stayId,
        fromRoomId: m.from,
        toRoomId: m.to,
        kind: this.kindOf(s, m.to),
        bedReady: bedStandsFor(this.fit, this.roomById.get(m.to)!, s.requestedBedConfig),
      };
    });
    const bends: RuleBend[] = [
      ...this.bendsOf({ ...T, id: T.id }, room.id, false),
      ...chain.flatMap((m) => this.bendsOf(this.byId.get(m.stayId)!, m.to, true)),
    ];
    if (chain.length > (this.options.maxMoves ?? RESHUFFLE_MAX_MOVES)) bends.push({ rule: 'maxMoves', stayId: T.id });

    const targetBedReady = bedStandsFor(this.fit, room, T.requestedBedConfig);
    // Net bed changes: a placement into a room not standing as asked costs one;
    // a moved stay leaving a room that did not stand as asked saves one.
    let bedChanges = targetBedReady === false ? 1 : 0;
    for (const m of moves) {
      const s = this.byId.get(m.stayId)!;
      if (m.bedReady === false) bedChanges += 1;
      if (bedStandsFor(this.fit, this.roomById.get(m.fromRoomId)!, s.requestedBedConfig) === false) bedChanges -= 1;
    }
    const strandedNights =
      this.stranded(TARGET, room.id, layout) + chain.reduce((n, m) => n + this.stranded(m.stayId, m.to, layout), 0);
    const offFloor = this.siblingFloors.length > 0 && !this.siblingFloors.includes(room.floor) ? 1 : 0;
    // The operator's order for who is upgraded: half board, B&B, then room
    // only; longer stays first. Only between otherwise-equal plans.
    let upgradeRank = 0;
    for (const m of moves) {
      if (m.kind !== 'upgrade') continue;
      const s = this.byId.get(m.stayId)!;
      const board = this.options.boardOf?.(s.ratePlanId) ?? 'none';
      upgradeRank += (BOARD_RANK[board] ?? 2) * 100 - Math.min(99, pmsNightCount(s.checkinDate, s.checkoutDate));
    }
    const roomNo = Number.parseInt(room.roomNumber, 10);

    const { released, ordered } = this.executionOrder(moves);
    return {
      targetRoomId: room.id,
      targetKind: this.kindOf({ ...T, roomId: null }, room.id),
      targetBedReady,
      released,
      moves: ordered,
      bends,
      cost: [
        bends.length,
        chain.length,
        chain.filter((m) => this.byId.get(m.stayId)!.checkinDate === this.tomorrow).length,
        bedChanges,
        strandedNights,
        offFloor,
        Number.isNaN(roomNo) ? Number.MAX_SAFE_INTEGER : roomNo,
        upgradeRank,
      ],
    };
  }

  /**
   * The order the desk can perform the moves in. A move can be made once every
   * stay sitting in its destination on those nights has left: a topological
   * order of « who must leave first ». A swap is a cycle in that order, and has
   * none — so a stay of the cycle is RELEASED first (no room for a moment), which
   * frees its room for the others, and is re-allocated in its turn.
   */
  private executionOrder(moves: ReshuffleMove[]): { released: string[]; ordered: ReshuffleMove[] } {
    const blockers = new Map<string, string[]>();
    for (const m of moves) {
      const s = this.byId.get(m.stayId)!;
      blockers.set(
        m.stayId,
        moves
          .filter((o) => o.stayId !== m.stayId && o.fromRoomId === m.toRoomId && overlaps(this.byId.get(o.stayId)!, s))
          .map((o) => o.stayId),
      );
    }
    const gone = new Set<string>(); // moved, or released
    const released: string[] = [];
    const ordered: ReshuffleMove[] = [];
    // Discovered from the target outwards; the far end of a chain goes first.
    let pending = [...moves].reverse();
    while (pending.length > 0) {
      const ready = pending.filter((m) => blockers.get(m.stayId)!.every((b) => gone.has(b) && (b !== m.stayId)));
      if (ready.length > 0) {
        for (const m of ready) {
          ordered.push(m);
          gone.add(m.stayId);
        }
        pending = pending.filter((m) => !ready.includes(m));
      } else {
        // A cycle: release its first stay, which frees that stay's room.
        const first = pending[0]!;
        released.push(first.stayId);
        gone.add(first.stayId);
      }
    }
    return { released, ordered };
  }

  // ── The split ────────────────────────────────────────────────────────────

  split(): { segments: SplitSegment[]; bends: RuleBend[] } | null {
    const T = this.target;
    const candidates = this.typed.filter((r) => this.allows({ ...T, id: TARGET }, r));
    const L = pmsNightCount(T.checkinDate, T.checkoutDate);
    const free = (roomId: RoomId, i: number) => {
      const night = { checkinDate: pmsAddDays(T.checkinDate, i), checkoutDate: pmsAddDays(T.checkinDate, i + 1) };
      return this.conflicts(roomId, night).length === 0;
    };
    const segments: SplitSegment[] = [];
    let i = 0;
    while (i < L) {
      let best: { room: AssignableRoom; run: number } | null = null;
      for (const room of candidates) {
        let run = 0;
        while (i + run < L && free(room.id, i + run)) run += 1;
        if (run > 0 && (best === null || run > best.run)) best = { room, run };
      }
      if (best === null) return null;
      segments.push({ roomId: best.room.id, from: pmsAddDays(T.checkinDate, i), to: pmsAddDays(T.checkinDate, i + best.run) });
      i += best.run;
    }
    if (segments.length < 2) return null; // one segment is a direct room: plans() would have found it
    const bends = segments.flatMap((g) => this.bendsOf({ ...T, id: T.id }, g.roomId, false));
    return { segments, bends: dedupeBends(bends) };
  }

  // ── Who has no bed, night by night ───────────────────────────────────────

  /** A matching, per night of the target's stay, of every stay holding that
   *  night (the target and other unassigned stays included) to a room, under
   *  this level's rules: fixed stays keep their room, the others take any room
   *  the rules allow. What it cannot seat is the deficit. A necessary condition
   *  for any plan, and with every rule lifted it is the overbooking's shortfall. */
  deficits(): { date: IsoDate; missing: number }[] {
    const T = this.target;
    const out: { date: IsoDate; missing: number }[] = [];
    const L = pmsNightCount(T.checkinDate, T.checkoutDate);
    for (let i = 0; i < L; i += 1) {
      const date = pmsAddDays(T.checkinDate, i);
      const night = { checkinDate: date, checkoutDate: pmsAddDays(date, 1) };
      const people: PlannerStay[] = [
        ...this.stays.filter(
          (s) => s.id !== T.id && overlaps(s, night) && (s.roomId === null || this.roomById.has(s.roomId)),
        ),
        { ...T, id: TARGET },
      ];
      const byId = new Map(people.map((p) => [p.id, p]));
      const options = (p: PlannerStay): AssignableRoom[] =>
        p.id !== TARGET && p.roomId !== null && this.fixed(p.id)
          ? [this.roomById.get(p.roomId)!]
          : this.typed.filter((r) => this.allows(p, r));
      const owner = new Map<RoomId, string>();
      const augment = (p: PlannerStay, seen: Set<RoomId>): boolean => {
        for (const r of options(p)) {
          if (seen.has(r.id)) continue;
          seen.add(r.id);
          const holder = owner.get(r.id);
          if (holder === undefined || augment(byId.get(holder)!, seen)) {
            owner.set(r.id, p.id);
            return true;
          }
        }
        return false;
      };
      let missing = 0;
      for (const p of people) if (!augment(p, new Set())) missing += 1;
      if (missing > 0) out.push({ date, missing });
    }
    return out;
  }
}

function dedupeBends(bends: RuleBend[]): RuleBend[] {
  const seen = new Set<string>();
  return bends.filter((b) => {
    const key = `${b.rule}|${b.stayId}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function comparePlans(a: ReshufflePlan, b: ReshufflePlan): number {
  for (let i = 0; i < a.cost.length; i += 1) {
    const d = a.cost[i]! - b.cost[i]!;
    if (d !== 0) return d;
  }
  return 0;
}
